import "./main.css";
import { CustomScript } from "./types";
import { h, render } from "preact";
import { z } from "zod";
import { fetchDataFromZoe } from "@rbmh-partner-projects/diesdas-zoe-modules";
import { mockZoeConfig } from "./mocks/zoeConfig";
import { Leaderboard } from "./Leaderboard";
import { fetchLeaderboard } from "./requestLeaderboardData";

export const { start, attach }: CustomScript = {
  /**
   * The start function is called when the custom script panel is rendered
   * (e.g. when the user navigates to a page where the custom script is configured).
   * This method must return a Promise that resolves with an object containing a stop function.
   * Do as much work as possible here, especially data loading. Contrary to attach(),
   * the start method is asynchronous and any work done here delays the processing of subsequent panels.
   * This minimizes layout shifts.
   */
  start: async (params) => {
    const parsedConfig = z
      .object({
        zoeId: z.string(),
        zoeEnv: z.string().default("production"),
        title: z.string(),
      })
      .safeParse(params.config);

    if (!parsedConfig.success) {
      console.error(
        `Error parsing config, missing required field "${parsedConfig.error.errors[0].path}"`,
      );
      return Promise.reject(parsedConfig.error);
    }
    const config = parsedConfig.data;

    const dataFromZoe = await fetchDataFromZoe({
      config,
      fallbackSchema: mockZoeConfig,
    });

    /**
     * For this component we can ignore everything the ZOE sends except the gameId.
     */
    const parsedZoeConfig = z
      .object({
        gameId: z.string(),
      })
      .safeParse(dataFromZoe);

    if (!parsedZoeConfig.success) {
      console.error(
        `Error parsing ZOE config, missing required field "${parsedZoeConfig.error.errors[0].path}"`,
      );
      return Promise.reject(parsedZoeConfig.error);
    }
    const zoeConfig = parsedZoeConfig.data;

    const highscoreServiceEndpoint =
      config.zoeEnv === "staging"
        ? "https://rb-prjct-crtns-gamebackend-s.herokuapp.com"
        : "https://rb-prjct-crtns-gamebackend-eu.herokuapp.com";

    let leaderboardData;

    try {
      leaderboardData = await fetchLeaderboard({
        highscoreServiceEndpoint,
        gameId: zoeConfig.gameId,
      });
    } catch (error) {
      console.error("Error fetching data for Leaderboard: ", error);
      return Promise.reject(error);
    }

    render(
      <Leaderboard leaderboardData={leaderboardData} title={config.title} />,
      params.el,
    );

    return {
      /**
       * The stop function is called when the custom script panel is stopped (e.g. when the user navigates to a new page).
       * Any changes the custom script makes to the document must be undone in this method,
       * otherwise they persist and may break following page visits.
       * This includes changes to panels, as panels can be reused on the following page under certain circumstances.
       * Please do any clean-up necessary so all traces that were here are gone.
       */
      stop: () => {
        console.log("stop");
      },
    };
  },
  /**
   * The attach function is called as soon as the custom script is ready to access the DOM elements.
   * If you need to modify DOM elements, make sure to implement your desired behavior in this function
   * to ensure that it doesn't access elements that are not yet set up in the DOM.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  attach: () => {},
};
